import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CONSTANTS } from 'src/app/core/constants/constants';
import { Mesh } from 'src/app/core/interfaces/mesh/mesh';
import { ReferenceSystemGraphicProperties } from 'src/app/core/models/3D/graphic-properties/reference-system-graphic-properties.model';
import { Connection } from 'src/app/core/models/connection.model';
import { NodeType } from 'src/app/core/models/load/node/node-type.enum';
import { Node } from 'src/app/core/models/load/node/node.model';
import { Point3d } from 'src/app/core/models/point3d.model';
import { ReferenceSystemType } from 'src/app/core/models/reference-system/reference-system-type.enum';
import { ReferenceSystem } from 'src/app/core/models/reference-system/reference-system.model';
import { Simulation } from 'src/app/core/models/simulation.model';
import { ObjectState } from 'src/app/shared/utils/object-state';
import { RemoveAllIds } from 'src/app/shared/utils/remove-all-ids/remove-all-ids';
import { DeleteRequestAnswer } from 'src/app/shared/utils/simulation-manager-aux/delete-request-answer';
import { DataSharingService } from '../../../data-sharing/data-sharing.service';
import { SimulationManagerService } from '../../simulation-manager.service';
import { MapsService } from '../maps/maps.service';
import { TitleCopyService } from '../title-copy/title-copy.service';

@Injectable({
  providedIn: 'root',
})
export class ReferenceSystemService {
  public currentReferenceSystemId$ = new BehaviorSubject<number>(null);

  constructor(
    private simulationService: SimulationManagerService,
    private dataSharingService: DataSharingService,
    private mapsService: MapsService,
    private titleCopyService: TitleCopyService,
  ) {}

  get simulation(): Simulation {
    return this.simulationService.getSimulation();
  }

  hasReferenceSystem(): boolean {
    return !!this.simulation?.referenceSystem?.length;
  }

  getById(id: number): ReferenceSystem {
    const answer = this.simulation?.referenceSystem?.find((ref) => ref.internalId === id);
    return answer ? answer : null;
  }

  getNew(): ReferenceSystem {
    this.create();
    return this.simulation.referenceSystem[this.simulation.referenceSystem.length - 1];
  }

  create(): void {
    const newReferenceSystem: ReferenceSystem = new ReferenceSystem();
    const id = this.simulationService.getNewId();

    newReferenceSystem.id = null;
    newReferenceSystem.internalId = id;
    newReferenceSystem.title = `ReferenceSystem-${id}`;
    newReferenceSystem.type = ReferenceSystemType.FIXED;
    newReferenceSystem.structureOfAttachment = -1;
    newReferenceSystem.nodeOfAttachment = -1;
    newReferenceSystem.definedByUser = true;
    newReferenceSystem.structureOfDefinition = -1;
    newReferenceSystem.elementOfDefinitionIndex = -1;
    newReferenceSystem.coordinates = new Point3d({ x: 0, y: 0, z: 0 });
    newReferenceSystem.rotation = new Point3d({ x: 0, y: 0, z: 0 });
    newReferenceSystem.needsUpdate = true;
    newReferenceSystem.graphicProperties = new ReferenceSystemGraphicProperties();

    if (this.simulation.referenceSystem) {
      this.simulation.referenceSystem.push(newReferenceSystem);
    } else {
      this.simulation.referenceSystem = [newReferenceSystem];
    }

    this.mapsService.saveInAllMaps(CONSTANTS.REFERENCE_SYSTEM, newReferenceSystem);
    this.simulationService.changeSimulation();
    this.dataSharingService.updateCatenary$.next();
  }

  duplicate(originalId: number): ReferenceSystem {
    const objectArray = this.simulation.referenceSystem;
    const originalObject = objectArray.find(({ internalId }) => originalId === internalId);
    const newReferenceSystem: ReferenceSystem = new ReferenceSystem();
    const newId = this.simulationService.getNewId();

    Object.assign(newReferenceSystem, originalObject);
    newReferenceSystem.internalId = newId;
    newReferenceSystem.title = this.titleCopyService.getCopyFromTitle(originalObject.title);
    newReferenceSystem.needsUpdate = true;
    newReferenceSystem.graphicProperties = new ReferenceSystemGraphicProperties();

    RemoveAllIds(newReferenceSystem);
    this.simulation.referenceSystem.push(newReferenceSystem);
    this.mapsService.saveInAllMaps(CONSTANTS.REFERENCE_SYSTEM, newReferenceSystem);
    this.simulationService.changeSimulation();

    return newReferenceSystem;
  }

  save(id: number, payload: any): void {
    const referenceSystem = this.getById(id);
    if (!referenceSystem || !payload) {
      return;
    }

    Object.assign(referenceSystem, payload);
    referenceSystem.needsUpdate = true;

    this.mapsService.updateObject(referenceSystem);
    this.simulationService.changeSimulation(false);
    this.dataSharingService.updateCatenary$.next();
  }

  delete(id: number): DeleteRequestAnswer {
    this.simulation.referenceSystem = this.simulation.referenceSystem.filter((ref) => {
      const matches = ref.internalId !== id;
      if (matches) {
        return true;
      }
      ref.graphicProperties?.delete();
      return false;
    });

    this.mapsService.deleteFromMaps(id);
    this.simulationService.changeSimulation();
    this.dataSharingService.updateCatenary$.next();

    return new DeleteRequestAnswer(ObjectState.objectWasDeleted, null);
  }

  resetByLineId(id: number): void {
    if (!this.simulation) {
      return;
    }
    this.simulation.referenceSystem = this.simulation.referenceSystem?.map((refSystem) => {
      const attachmentMatches = refSystem.structureOfAttachment === id;
      const definitionMatches = refSystem.structureOfDefinition === id;

      if (!attachmentMatches && !definitionMatches) {
        return refSystem;
      }

      if (attachmentMatches) {
        refSystem.type = ReferenceSystemType.FIXED;
        refSystem.structureOfAttachment = -1;
        refSystem.nodeOfAttachment = -1;
        refSystem.coordinates = new Point3d({ x: 0, y: 0, z: 0 });
      }

      if (definitionMatches) {
        refSystem.definedByUser = true;
        refSystem.structureOfDefinition = -1;
        refSystem.elementOfDefinitionIndex = -1;
        refSystem.rotation = new Point3d({ x: 0, y: 0, z: 0 });
      }

      this.mapsService.updateObject(refSystem);
      return refSystem;
    });
    this.simulationService.changeSimulation(false);
  }

  resetByConnectionNodeId(ids: Set<number>): void {
    if (!this.simulation) {
      return;
    }

    this.simulation.referenceSystem = this.simulation.referenceSystem?.map((refSystem) => {
      const forConnection = refSystem.structureOfAttachment === -1;
      const attachmentMatches = ids.has(refSystem.nodeOfAttachment);

      if (!forConnection || !attachmentMatches) {
        return refSystem;
      }

      refSystem.type = ReferenceSystemType.FIXED;
      refSystem.structureOfAttachment = -1;
      refSystem.nodeOfAttachment = -1;
      refSystem.coordinates = new Point3d({ x: 0, y: 0, z: 0 });
      this.mapsService.updateObject(refSystem);
      return refSystem;
    });
    this.simulationService.changeSimulation(false);
  }

  resetAllByMesh(
    mesh: Mesh[],
    connectionAsGlobalFactory: (connection: Connection) => Connection,
  ): void {
    if (!mesh) {
      return;
    }

    this.simulation.referenceSystem.forEach((refSystem: ReferenceSystem) => {
      if (refSystem.type === ReferenceSystemType.ATTACHED_TO_NODE) {
        this.updateReferenceSystemCoordinates(refSystem, mesh, connectionAsGlobalFactory);
      }
      if (!refSystem.definedByUser) {
        this.updateReferenceSystemAngles(refSystem, mesh);
      }
    });

    this.simulationService.changeSimulation(false);
  }

  updateReferenceSystemCoordinates(
    refSystem: ReferenceSystem,
    mesh: Mesh[],
    connectionAsGlobalFactory: any,
  ): void {
    const { structureOfAttachment, nodeOfAttachment } = refSystem;
    const forConnection = structureOfAttachment === -1;
    const hasNode = nodeOfAttachment !== -1;

    if (!hasNode) {
      refSystem.coordinates = new Point3d();
      return;
    }

    const node = this.simulation.node.find(({ internalId }) => internalId === nodeOfAttachment);

    if (!node) {
      return;
    }
    const isAnchor = node.type === NodeType.ANCHOR;

    if (forConnection) {
      refSystem.coordinates = this.getConnectionNodeCoordinates(
        node,
        mesh,
        connectionAsGlobalFactory,
      );
      return;
    }

    if (isAnchor) {
      refSystem.coordinates = this.getAnchorNodeCoordinates(node, mesh);
      return;
    }

    refSystem.coordinates = this.getSegmentNodeCoordinates(node, mesh);
  }

  getConnectionNodeCoordinates(node: Node, mesh: Mesh[], connectionAsGlobalFactory: any): Point3d {
    const firstUsingLine = this.simulation.line.find(
      ({ connectionId }) => connectionId === node.referenceId,
    );

    if (!firstUsingLine) {
      const connection = this.simulation.connection.find(
        ({ internalId }) => internalId === node.referenceId,
      );
      const coordinates = new Point3d();
      if (!connection) {
        return coordinates;
      }

      const { localCoordinates } = connectionAsGlobalFactory(connection);
      return Object.assign(coordinates, localCoordinates);
    }

    const { webNodes } = mesh.find(({ lineId }) => lineId === firstUsingLine.internalId);
    const { x, y, z } = webNodes.find(({ label }) => label === node.title);
    return new Point3d({ x, y, z });
  }

  getAnchorNodeCoordinates({ referenceId, title }: Node, mesh: Mesh[]): Point3d {
    const { webNodes } = mesh.find(({ lineId }) => lineId === referenceId);
    const { x, y, z } = webNodes.find(({ label }) => label === title);
    return new Point3d({ x, y, z });
  }

  getSegmentNodeCoordinates(node: Node, mesh: Mesh[]): Point3d {
    const { referenceId, title } = node;
    const firstUsingLine = this.simulation.line.find(
      ({ internalId }) => internalId === referenceId,
    );
    const { webNodes } = mesh.find(({ lineId }) => lineId === referenceId);
    const segIndexFromTitle = this.getSegmentIndexFromNodeTitle(title);
    const reversedSegIndex = firstUsingLine.segConfig.length - segIndexFromTitle;
    const { x, y, z } = webNodes.find(({ label }) => {
      return (
        label ===
        `${firstUsingLine.title}${CONSTANTS.SEG_AFFIX}${this.getSegIndexPreffix(
          reversedSegIndex,
        )}${reversedSegIndex}`
      );
    });
    return new Point3d({ x, y, z });
  }

  getSegmentIndexFromNodeTitle(title: string): number {
    const splittedTitle = title.split(`${CONSTANTS.SEG_AFFIX}_`);
    return +splittedTitle[splittedTitle.length - 1];
  }

  getSegIndexPreffix(reversedSegIndex: number): string {
    const indexString = String(reversedSegIndex);
    let preffix = '';
    while (preffix.length + indexString.length < 3) {
      preffix += '0';
    }
    return preffix;
  }

  updateReferenceSystemAngles(refSystem: ReferenceSystem, mesh: Mesh[]): void {
    const selectedLineId = refSystem.structureOfDefinition;
    if (selectedLineId === -1 || refSystem.elementOfDefinitionIndex === -1) {
      refSystem.rotation = new Point3d({ x: 0, y: 0, z: 0 });
      return;
    }

    const lineOfDefinition = mesh.find(({ lineId }) => lineId === selectedLineId);

    refSystem.rotation = lineOfDefinition.elements[refSystem.elementOfDefinitionIndex].angles;
  }
}
